/**
 * PDF 查看器组件 - 基于 pdfjs-dist Canvas 高清渲染
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface Props { url: string }

export default function PdfViewer({ url }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);
  const renderTaskRef = useRef<any>(null);
  const mountedRef = useRef(false);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // 加载 PDF
  useEffect(() => {
    setLoading(true); setError(''); setPageNum(1);
    let cancelled = false;
    (async () => {
      try {
        const doc = await pdfjsLib.getDocument({ url }).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setTotalPages(doc.numPages);
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) { setError(e.message || '加载失败'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // 渲染页面（高清）
  const renderPage = useCallback(async (num: number) => {
    const doc = pdfDocRef.current;
    if (!doc || !canvasRef.current || !containerRef.current) return;
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch {} }

    try {
      const page = await doc.getPage(num);
      const canvas = canvasRef.current;
      const container = containerRef.current;
      const dpr = window.devicePixelRatio || 2;
      const containerWidth = container.clientWidth - 16;
      const vp = page.getViewport({ scale: 1 });
      const fitScale = containerWidth / vp.width;
      const renderScale = fitScale * dpr;
      const viewport = page.getViewport({ scale: renderScale });

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.height = `${viewport.height / dpr}px`;
      canvas.style.width = `${viewport.width / dpr}px`;

      const ctx = canvas.getContext('2d')!;
      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      await task.promise;
    } catch (e: any) {
      if (e?.name !== 'RenderingCancelledException') console.error('PDF render error:', e);
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      // 延迟渲染确保 canvas 已挂载到 DOM
      requestAnimationFrame(() => requestAnimationFrame(() => renderPage(pageNum)));
    }
  }, [pageNum, loading, renderPage]);

  useEffect(() => {
    const onResize = () => renderPage(pageNum);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pageNum, renderPage]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12, color: '#999' }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
        <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: '.8125rem' }}>加载文档中...</span>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 8, color: '#e53e3e', padding: 20 }}>
      <p style={{ fontSize: '.875rem' }}>{error}</p>
      <a href={url} target="_blank" style={{ color: 'var(--cinnabar-500)', fontSize: '.8125rem' }}>点击下载查看</a>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#525659' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '8px 12px', background: '#333', color: '#ccc', flexShrink: 0 }}>
        <button onClick={() => setPageNum(p => Math.max(1, p - 1))} disabled={pageNum <= 1}
          style={{ background: 'none', border: 'none', color: pageNum > 1 ? '#ccc' : '#555', cursor: pageNum > 1 ? 'pointer' : 'default', padding: 4 }}>
          <ChevronLeft size={20} />
        </button>
        <span style={{ fontSize: '.8125rem', minWidth: 60, textAlign: 'center' }}>{pageNum} / {totalPages}</span>
        <button onClick={() => setPageNum(p => Math.min(totalPages, p + 1))} disabled={pageNum >= totalPages}
          style={{ background: 'none', border: 'none', color: pageNum < totalPages ? '#ccc' : '#555', cursor: pageNum < totalPages ? 'pointer' : 'default', padding: 4 }}>
          <ChevronRight size={20} />
        </button>
      </div>
      <div ref={containerRef} style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: 8 }}>
        <canvas ref={canvasRef} style={{ boxShadow: '0 2px 8px rgba(0,0,0,.3)' }} />
      </div>
    </div>
  );
}
